import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
export default function FilterDrawer({
  open,
  onClose,
  onClear,
  count,
  children,
}) {
  const closeButton = useRef(null);
  const dialog = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const key = (event) => {
      if (event.key === "Escape") close.current();
      if (event.key === "Tab") {
        const controls = dialog.current?.querySelectorAll(
          "button:not([disabled]), input:not([disabled]), select:not([disabled])",
        );
        if (!controls?.length) return;
        const first = controls[0],
          last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    const resize = () => {
      if (window.innerWidth >= 1024) close.current();
    };
    window.addEventListener("resize", resize);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", resize);
      previous?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 lg:hidden">
      <button
        aria-label="Close filter overlay"
        onClick={onClose}
        className="absolute inset-0 h-full w-full"
      />
      <section
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Product filters"
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-xl font-semibold text-primary-900">Filters</h2>
            <p className="text-xs text-slate-500">
              {count} matching {count === 1 ? "product" : "products"}
            </p>
          </div>
          <button
            ref={closeButton}
            aria-label="Close filters"
            onClick={onClose}
            className="rounded-full bg-slate-50 p-2"
          >
            <X size={20} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-6">
          {children}
        </div>
        <footer className="flex gap-3 border-t border-slate-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            onClick={onClear}
            className="rounded-xl border border-primary-200 px-4 py-3 text-sm font-semibold text-primary-800"
          >
            Clear all
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-xl bg-primary-800 px-4 py-3 text-sm font-semibold text-white"
          >
            Show {count} {count === 1 ? "result" : "results"}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
